import React, { useState, useEffect, useRef, lazy, Suspense } from "react";
import { Link, useNavigate, useLocation, useParams } from "react-router-dom";
import { toast } from "sonner";
import ErrorState from "../../components/primitives/ErrorState";
import { classifyApiError } from "../../hooks/useApiList";
import { getInitials } from "../../components/UserAvatar";
import { useAuth } from "../../context/AuthContext";
import { memoriserRetour } from "../../utils/murConnexion";
import { api } from "../../context/AuthContext";
import { useAssociationAuth } from "../../context/AssociationAuthContext";
import { ListingCard, ListingCardSkeleton, CategoryPills } from "../../components/ListingCard";
import CritereClassement from "../../components/CritereClassement";
import { MobileHeader } from "../../components/Layout";
import { SmartSearchBar } from "../../components/SmartSearch";
// Compact community widget - not the heavy leaderboard
import { TEINTE_PILIER, teintePilier } from "../../constants/piliers";
import SEO, { organizationJsonLd } from "../../components/SEO";
import HomeBlogSection from "../../components/HomeBlogSection";
import PressBadge from "../../components/PressBadge";
import ImpactScoreEncart from "../../components/ImpactScoreEncart";
import { ligneAutresAnnonces } from "../../utils/ligneAutresAnnonces";
import { safeLocalStorage, safeSessionStorage } from "../../utils/safeStorage";
import { CATEGORIES_SEO } from "../../constants/categoriesProgrammatic";
import { pastelCover } from "../../components/AssociationCard";
import { slugify } from "../../utils/slugify";
import { PRESTATAIRE_LIVRAISON, RESEAUX_RELAIS_TEXTE } from "../../constants/livraison";
import { imageReduite } from "../../utils/imageReduite";

// Map id catégorie backend -> slug SEO (pour lier "Tout voir" vers la
// page catégorie programmatique /categorie/:slug). Fallback /search.
const CAT_SLUG = Object.fromEntries(CATEGORIES_SEO.map((c) => [c.id, c.slug]));
const categoryHref = (catId) =>
  CAT_SLUG[catId] ? `/categorie/${CAT_SLUG[catId]}` : `/search?category=${catId}`;

// Personnalisation : on retient les catégories récemment consultées
// (clic sur une annonce ou sélection d'un filtre) pour remonter ces
// lignes en haut de la home — l'accueil s'adapte aux dernières
// recherches. 100 % côté client, aucun tracking serveur.
const RECENT_CATS_KEY = "gively_recent_cats";
const getRecentCats = () => {
  try { return JSON.parse(safeLocalStorage.getItem(RECENT_CATS_KEY) || "[]"); }
  catch { return []; }
};
const pushRecentCat = (catId) => {
  if (!catId) return;
  try {
    let arr = getRecentCats().filter((c) => c !== catId);
    arr.unshift(catId);
    safeLocalStorage.setItem(RECENT_CATS_KEY, JSON.stringify(arr.slice(0, 6)));
  } catch { /* storage indispo */ }
};
const orderRowsByRecent = (rows) => {
  const recent = getRecentCats();
  if (!recent.length) return rows;
  const score = (cat) => {
    const i = recent.indexOf(cat);
    return i === -1 ? 999 : i;
  };
  return [...rows].sort((a, b) => score(a.category) - score(b.category));
};

// On n'affiche que les 3 catégories LES PLUS FOURNIES (le backend renvoie
// déjà les lignes triées par nombre d'annonces décroissant). On exige un
// minimum d'annonces par ligne pour éviter les lignes quasi vides ("trou
// blanc"). Puis on réordonne ces 3 lignes selon les catégories récemment
// consultées par l'utilisateur.
const MIN_PER_ROW = 3;
const MAX_HOME_ROWS = 3;
const selectTopRows = (rows) => {
  const eligible = rows.filter((r) => (r.listings?.length || 0) >= MIN_PER_ROW);
  const base = (eligible.length ? eligible : rows).slice(0, MAX_HOME_ROWS);
  return orderRowsByRecent(base);
};


// Une ligne de catégorie : titre + lien "Tout voir" + bande d'annonces
// défilante horizontalement (cartes compactes).
// Ligne générique d'annonces défilante (titre + lien + bande horizontale).
const ScrollRow = ({ title, seeAllTo, listings, onCardClick }) => {
  if (!listings || listings.length === 0) return null;
  return (
    <div className="mb-7">
      {(title || seeAllTo) && (
        <div className={`flex items-center mb-3 ${title ? "justify-between" : "justify-end"}`}>
          {title && <h2 className="text-lg font-bold text-slate-900">{title}</h2>}
          {seeAllTo && (
            <Link to={seeAllTo} className="text-sm text-[#1DA1F2] font-medium hover:underline shrink-0 ml-3">
              Tout voir →
            </Link>
          )}
        </div>
      )}
      <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide snap-x -mx-4 px-4">
        {listings.map((l) => (
          <div
            key={l.listing_id}
            onClick={onCardClick ? () => onCardClick(l) : undefined}
            className="snap-start shrink-0 w-36 sm:w-44"
          >
            <ListingCard listing={l} />
          </div>
        ))}
      </div>
    </div>
  );
};

const CategoryRow = ({ row }) => (
  <ScrollRow
    title={row.category_label}
    seeAllTo={categoryHref(row.category)}
    listings={row.listings}
    onCardClick={() => pushRecentCat(row.category)}
  />
);

const CategoryRowSkeleton = () => (
  <div className="mb-7">
    <div className="h-5 w-32 bg-slate-200 rounded mb-3 animate-pulse" />
    <div className="flex gap-3 overflow-hidden">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="shrink-0 w-36 sm:w-44">
          <ListingCardSkeleton />
        </div>
      ))}
    </div>
  </div>
);

// Onglets de TYPE d'annonce (Vente / Location / Services) — filtre principal de
// la home. Re-cliquer l'onglet actif revient à l'accueil par défaut.
const TYPE_TABS = [
  { id: "sale", label: "Vente" },
  { id: "rental", label: "Location" },
  { id: "service", label: "Services" },
];

const TYPE_VIEW = {
  sale: { title: "Annonces à la vente", emoji: "🛍️", empty: "Aucune annonce à la vente pour le moment" },
  rental: { title: "Annonces de location", emoji: "🔑", empty: "Aucune location pour le moment" },
  service: { title: "Annonces services", emoji: "🤝", empty: "Aucun service proposé pour le moment" },
};

// Grille spécifique ACCUEIL (demande Lucas, juin 2026) : cartes plus grandes
// et plus aérées — 2 par rangée sur mobile (au lieu de 3 serrées), plus
// d'espacement. Reste 3/4 sur grand écran. Ne touche PAS la grille partagée
// LISTING_GRID (recherche, pages SEO…).
const HOME_GRID = "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4";
// Accueil = aperçu : 4 rangées de 2 = 8 annonces, puis « Tout voir ».
const HOME_PREVIEW_COUNT = 8;

// SIGNATURE GIVELY (30/09/2026, Lucas : « les boutons doivent rester bleus :
// Vente, Location, Services. Mais quand on clique sur Location ça devient
// violet, et quand on clique sur Services ça devient vert — c'est la signature
// de la plateforme »). Au repos : les trois en bleu. Sélectionné : la couleur
// de SON pilier (vente bleu, location violet, services vert), prise dans la
// table unique `constants/piliers.js`, plus un anneau qui distingue « Vente
// sélectionné » de « Vente au repos ». Les fonds sont ceux dont le texte blanc
// passe le contraste AA (#0B6FA4, #7C3AED, #047857).
const BLEU_REPOS = TEINTE_PILIER.sale.texte;
const ListingTypeTabs = ({ active, onSelect }) => (
  <div role="tablist" aria-label="Filtrer par type d'annonce" className="flex gap-2">
    {TYPE_TABS.map((t) => {
      const isActive = active === t.id;
      const couleur = isActive ? teintePilier(t.id).texte : BLEU_REPOS;
      return (
        <button
          key={t.id}
          role="tab"
          aria-selected={isActive}
          aria-controls="home-listings-panel"
          onClick={() => onSelect(isActive ? null : t.id)}
          data-testid={`type-tab-${t.id}`}
          data-pilier-actif={isActive ? t.id : undefined}
          style={{ backgroundColor: couleur, "--tw-ring-color": couleur }}
          className={`flex-1 py-2.5 rounded-xl text-sm font-bold text-white transition-all duration-200 shadow-sm hover:brightness-110 ${
            isActive ? "ring-2 ring-offset-2 shadow-md scale-[1.02]" : ""
          }`}
        >
          {t.label}
        </button>
      );
    })}
  </div>
);

// Generic stock photos used to fill empty asso cover slots —
// removed because they misrepresented the real associations
// ("photos d'illustration" cf. demande prod). Empty cover now
// renders a clean gradient instead. See render below.

// Carte compacte d'une association (utilisée dans le bandeau défilant).
const PartnerCard = ({ asso }) => (
  <Link
    to={`/association/${slugify(asso.name, asso.association_id)}`}
    className="shrink-0 w-44 bg-white rounded-xl shadow-sm border border-slate-100 hover:shadow-md hover:border-[#1DA1F2] transition-all overflow-hidden group"
    aria-label={asso.name}
  >
    <div className={`h-14 bg-gradient-to-br ${pastelCover(asso.association_id)} relative overflow-hidden`}>
      {asso.cover_image && (
        <img src={imageReduite(asso.cover_image, 352)} alt={`Bannière ${asso.name}`} loading="lazy" decoding="async"
          width="176" height="56"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
      )}
    </div>
    <div className="px-3 -mt-5 relative z-10">
      <div className="w-10 h-10 rounded-full bg-white border-2 border-white shadow-sm flex items-center justify-center overflow-hidden">
        {asso.logo ? (
          <img src={imageReduite(asso.logo, 96)} alt={`Logo ${asso.name}`} loading="lazy" decoding="async"
            width="40" height="40"
            className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#1DA1F2] to-violet-500 flex items-center justify-center text-white font-bold">
            {/* Helper PARTAGÉ : une seule lettre perdait la distinction entre
                deux associations voisines, et le repli « A » INVENTAIT une
                initiale pour une association sans nom. Le dégradé de marque
                est conservé ici (surface publique, un logo par carte avec le
                nom juste dessous) : seules les initiales sont unifiées. */}
            {getInitials(asso.name)}
          </div>
        )}
      </div>
    </div>
    <div className="px-3 pb-3 pt-1">
      <p className="font-semibold text-slate-900 text-sm truncate">{asso.name}</p>
      <p className="text-xs text-slate-500 truncate">{asso.city}</p>
    </div>
  </Link>
);

const PartnersWidget = () => {
  const [partners, setPartners] = useState([]);
  const trackRef = useRef(null);
  const pausedRef = useRef(false);
  const posRef = useRef(0);

  useEffect(() => {
    api.get("/associations", { params: { limit: 20 } })
      .then(r => setPartners((r.data || []).filter(a => !a.is_test).slice(0, 14)))
      .catch(() => {});
  }, []);

  // Défilement automatique doux (droite → gauche) via requestAnimationFrame.
  // La liste est dupliquée : quand on dépasse la moitié, on revient en
  // arrière de la moitié → boucle parfaitement continue, sans saccade.
  // Pause au survol / pendant un swipe ; respecte prefers-reduced-motion.
  useEffect(() => {
    const el = trackRef.current;
    if (!el || partners.length === 0) return undefined;
    const reduce = window.matchMedia
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return undefined;
    const SPEED = 0.4; // px / frame ≈ 24 px/s, défilement très doux
    let raf;
    const step = () => {
      if (el) {
        if (pausedRef.current) {
          posRef.current = el.scrollLeft; // resynchronise après swipe manuel
        } else {
          const half = el.scrollWidth / 2;
          posRef.current += SPEED;
          if (half > 0 && posRef.current >= half) posRef.current -= half;
          el.scrollLeft = posRef.current;
        }
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [partners]);

  if (partners.length === 0) return null;

  const pause = () => { pausedRef.current = true; };
  const resumeSoon = () => { setTimeout(() => { pausedRef.current = false; }, 1500); };
  // Liste dupliquée pour la boucle continue.
  const loop = [...partners, ...partners];

  return (
    <div className="py-8">
      <div className="text-center mb-5 px-4">
        <h2 className="text-xl font-bold text-slate-900">Nos partenaires associatifs</h2>
        <p className="text-sm text-slate-500 mt-1">Ils nous font confiance pour recevoir des dons solidaires</p>
      </div>
      <div
        ref={trackRef}
        className="flex gap-3 overflow-x-auto scrollbar-hide px-4"
        onMouseEnter={pause}
        onMouseLeave={() => { pausedRef.current = false; }}
        onTouchStart={pause}
        onTouchEnd={resumeSoon}
        onPointerDown={pause}
        onPointerUp={resumeSoon}
      >
        {loop.map((asso, i) => (
          <PartnerCard key={`${asso.association_id}-${i}`} asso={asso} />
        ))}
      </div>
      <div className="text-center mt-5">
        <Link to="/associations" className="text-sm text-[#1DA1F2] font-semibold hover:underline">
          Voir toutes les associations →
        </Link>
      </div>
    </div>
  );
};

const HomePage = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState(null);
  // Filtre par TYPE (onglets Vente/Location/Services) — exclusif avec category.
  const [listingType, setListingType] = useState(null);
  const [typeListings, setTypeListings] = useState([]);
  const [typeLoading, setTypeLoading] = useState(false);
  const [globalRatings, setGlobalRatings] = useState(null);
  // Lignes par catégorie (vue par défaut, sans filtre).
  const [homeRows, setHomeRows] = useState([]);
  const [autresAnnonces, setAutresAnnonces] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(true);
  // Ligne spéciale en tête : favoris de l'utilisateur.
  const [favoritesRow, setFavoritesRow] = useState([]);
  // PANNE != CATALOGUE VIDE, sur la PREMIERE page que voit un visiteur qui ne
  // connait pas Gively. Les trois sources d'annonces de l'accueil retombaient
  // toutes sur leur branche VIDE en cas d'echec, et cette branche ecrit
  // « Aucune annonce disponible » / « Soyez le premier a publier ! » avec un
  // bouton « Publier une annonce ». Autrement dit : pendant une panne d'API,
  // la marketplace se presentait comme VIDE a un nouveau visiteur, et
  // l'invitait a creer une annonce en doublon d'un inventaire qu'elle n'avait
  // pas su lire (action de creation que ErrorState.js interdit explicitement
  // en etat d'erreur). Un etat par source : une panne des locations ne doit
  // pas eteindre l'accueil entier.
  const [rowsErrKind, setRowsErrKind] = useState(null);
  const [typeErrKind, setTypeErrKind] = useState(null);
  const [featuredErrKind, setFeaturedErrKind] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  // Une asso connectée a user=null (exclusion mutuelle des sessions) : sans
  // ça, l'accueil lui affichait le bandeau guest "créez un compte".
  const { account: assoAccount } = useAssociationAuth() || {};

  // Vue par défaut (aucun filtre) : annonces groupées par catégorie.
  useEffect(() => {
    if (category) return; // en mode filtré on utilise la grille ci-dessous
    const cached = safeLocalStorage.getItem("home_rows");
    const cachedT = safeLocalStorage.getItem("home_rows_t");
    if (cached && cachedT && Date.now() - parseInt(cachedT) < 120000) {
      try {
        const toutes = JSON.parse(cached);
        const top = selectTopRows(toutes);
        setHomeRows(top);
        setAutresAnnonces(ligneAutresAnnonces(toutes, top));
        setRowsLoading(false);
      }
      catch { /* cache corrompu */ }
    }
    api.get("/listings/home-rows", { params: { per: 12 }, timeout: 10000 })
      .then((res) => {
        const rows = Array.isArray(res.data) ? res.data : [];
        const top = selectTopRows(rows);
        setHomeRows(top);
        setAutresAnnonces(ligneAutresAnnonces(rows, top));
        setRowsErrKind(null);
        setRowsLoading(false);
        safeLocalStorage.setItem("home_rows", JSON.stringify(rows));
        safeLocalStorage.setItem("home_rows_t", Date.now().toString());
      })
      .catch((e) => { setRowsErrKind(classifyApiError(e)); setRowsLoading(false); });
  }, [category]);

  // Filtre par TYPE (onglet Vente/Location/Services) → on n'affiche QUE les
  // annonces du type choisi. Endpoint /listings?listing_type= (indexé, caché).
  // Cache localStorage 120 s pour une bascule instantanée entre onglets.
  useEffect(() => {
    if (!listingType) return;
    // FUITE ENTRE FILTRES (corrigée) : `typeListings` n'était jamais remis à
    // zéro au changement d'onglet, et la garde du squelette exige une liste
    // VIDE pour s'afficher. Résultat : dès qu'un onglet avait réussi, le
    // suivant montrait les annonces du PRÉCÉDENT pendant tout le chargement —
    // des annonces de VENTE sous le titre « Annonces de location », sans le
    // moindre indice. On repart d'une liste vide à chaque bascule.
    setTypeListings([]);
    setTypeErrKind(null);
    setTypeLoading(true);
    const ck = `listings_type_${listingType}`;
    try {
      const cached = safeLocalStorage.getItem(ck);
      const cachedT = safeLocalStorage.getItem(ck + "_t");
      if (cached && cachedT && Date.now() - parseInt(cachedT) < 120000) {
        setTypeListings(JSON.parse(cached));
        setTypeLoading(false);
      }
    } catch { /* cache corrompu */ }
    api.get("/listings", { params: { listing_type: listingType, limit: 24 }, timeout: 10000 })
      .then((res) => {
        const items = Array.isArray(res.data) ? res.data : [];
        setTypeListings(items);
        setTypeErrKind(null);
        setTypeLoading(false);
        try {
          safeLocalStorage.setItem(ck, JSON.stringify(items));
          safeLocalStorage.setItem(ck + "_t", Date.now().toString());
        } catch { /* quota */ }
      })
      .catch((e) => { setTypeErrKind(classifyApiError(e)); setTypeLoading(false); });
  }, [listingType]);

  // Ligne "Coups de cœur" = les annonces que l'utilisateur a likées.
  // N'apparaît QUE s'il a au moins un favori ; disparaît s'il retire tous
  // ses likes. Refetch quand le nombre de favoris change (like/unlike).
  const favCount = user?.favorites?.length || 0;
  useEffect(() => {
    if (category) return;
    if (!user) { setFavoritesRow([]); return; }
    api.get("/favorites", { timeout: 8000 })
      .then((r) => setFavoritesRow(Array.isArray(r.data) ? r.data : []))
      .catch(() => setFavoritesRow([]));
  }, [category, user, favCount]);

  // Phase 12.5 — agregat des notes pour AggregateRating Schema.org.
  // Si zero rating publie, l'API renvoie ratingCount=0 et le helper
  // organizationJsonLd skip l'injection (Google rejette ratingValue null).
  useEffect(() => {
    api.get("/stats/global-ratings", { timeout: 5000 })
      .then(r => setGlobalRatings(r.data || null))
      .catch(() => setGlobalRatings(null));
  }, []);

  useEffect(() => {
    // En vue par défaut, ce sont les lignes par catégorie (home-rows)
    // qui s'affichent — on ne fetch /featured QUE pour la vue filtrée.
    if (!category) { setLoading(false); return; }
    setLoading(true);
    const params = { category };

    const cacheKey = `listings_featured_${category}`;
    const cached = safeLocalStorage.getItem(cacheKey);
    const cachedTime = safeLocalStorage.getItem(cacheKey + "_t");
    const cacheAge = cachedTime ? (Date.now() - parseInt(cachedTime)) : 999999;

    if (cached && cacheAge < 120000) {
      try { setListings(JSON.parse(cached)); } catch { /* corrupted cache */ }
      setLoading(false);
      if (cacheAge < 30000) {
        return;
      }
    }

    api.get("/listings/featured", { params, timeout: 10000 }).then(res => {
      const fresh = Array.isArray(res.data) ? res.data : [];
      setListings(fresh);
      setFeaturedErrKind(null);
      setLoading(false);
      safeLocalStorage.setItem(cacheKey, JSON.stringify(fresh));
      safeLocalStorage.setItem(cacheKey + "_t", Date.now().toString());
    }).catch((e) => { setFeaturedErrKind(classifyApiError(e)); setLoading(false); });

  }, [category]);

  // Déduplication inter-lignes : une annonce ne doit apparaître qu'UNE
  // fois sur la home (priorité favoris → proximité → catégories). Évite
  // qu'une même annonce s'affiche dans 2 rubriques (et, avant le fix
  // cache, à 2 prix différents).
  const _seen = new Set();
  const _dedupe = (arr) => (arr || []).filter((l) => {
    if (!l || !l.listing_id || _seen.has(l.listing_id)) return false;
    _seen.add(l.listing_id);
    return true;
  });
  // Ordre = ordre d'affichage (favoris, puis catégories).
  const favoritesRowD = _dedupe(favoritesRow);
  const homeRowsD = homeRows
    .map((r) => ({ ...r, listings: _dedupe(r.listings) }))
    .filter((r) => r.listings.length > 0);
  const autresAnnoncesD = _dedupe(autresAnnonces);

  // Inventaire faible : les lignes horizontales (catégories + proximité)
  // laissent de gros vides à droite. Tant qu'on n'a pas assez d'annonces,
  // on affiche UNE grille 2 colonnes compacte de toutes les annonces, et
  // on rebascule automatiquement sur les lignes par catégorie au-delà du
  // seuil. Le catalogue complet est calculé indépendamment de la dédup
  // ci-dessus (sinon la ligne proximité viderait la grille).
  const _seenAll = new Set();
  const allListings = homeRows.flatMap((r) => r.listings || []).filter((l) => {
    if (!l || !l.listing_id || _seenAll.has(l.listing_id)) return false;
    _seenAll.add(l.listing_id);
    return true;
  });
  const lowInventory = allListings.length > 0 && allListings.length < 16;

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-100 via-emerald-50 to-violet-200" data-testid="home-page">
      <SEO
        title="Gively — Seconde main, location & services solidaires | Chaque transaction finance une association"
        description={`Achetez, vendez et louez d'occasion, ou proposez vos services et soutenez une association à chaque transaction. Économie circulaire, locale et solidaire : vous choisissez la cause. Paiement sécurisé, livraison suivie en point relais via ${PRESTATAIRE_LIVRAISON} (${RESEAUX_RELAIS_TEXTE}).`}
        path="/"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Gively",
            "alternateName": ["Marketplace Solidaire", "Marketplace ESS"],
            "url": "https://www.gively.fr",
            "inLanguage": "fr-FR",
            "description": "Marketplace française solidaire : achat, vente et location d'occasion, services entre voisins. Économie circulaire, locale et environnementale. Chaque transaction finance une association choisie par le vendeur.",
            "keywords": "marketplace solidaire, seconde main, économie circulaire, ESS, économie sociale et solidaire, dons associations, reconditionné, RSE, local, location entre particuliers, louer un objet, services solidaires, location solidaire",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://www.gively.fr/search?q={search_term_string}",
              "query-input": "required name=search_term_string",
            },
            "sameAs": [
              "https://www.facebook.com/gively.fr",
              "https://www.instagram.com/gively.fr",
              "https://www.linkedin.com/company/assomart/",
            ],
          },
          organizationJsonLd(globalRatings),
        ]}
      />
      {/* Header mobile en variante `blur` (barre fixe translucide) plutôt que
          `transparent` : le logo Gively reste CLAIREMENT fixe en haut et le texte
          du hero ne passe plus PAR-DESSUS au scroll (collision signalée par Lucas). */}
      <MobileHeader blur />

      {/* Hero — repositionnement ESS (mai 2026, retour Addi). On parle
          explicitement de seconde main, économie circulaire, locale et
          solidaire pour que Google comme l'utilisateur comprennent qu'on
          n'est PAS une marketplace classique mais une marketplace solidaire. */}
      <header className="px-4 pt-10 pb-8">
        <div className="container mx-auto max-w-3xl text-center">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-3 leading-tight">
            La marketplace <span className="text-[#1DA1F2]">solidaire</span> :
          </h1>
          <p className="text-slate-600 text-base sm:text-lg mb-6 max-w-xl mx-auto">
            Une part de chaque transaction est reversée à l'association de votre choix.{" "}
            <Link to="/comment-ca-marche" className="text-[#1DA1F2] font-semibold hover:underline">
              Comment ça marche ?
            </Link>
          </p>



          {/* Compteur de dons cumulés depuis le lancement — preuve sociale
              + signature solidaire (remplace l'ancienne frise « comment ça
              marche »). Source : /stats/yearly-impact (cumul all-time). */}
          <HeroDonationCounter />
          <SmartSearchBar
            placeholder="Que cherchez-vous ?"
            showLocationInput={true}
            onSearch={({ query, location }) => {
              const p = new URLSearchParams();
              if (query) p.set("q", query);
              if (location) {
                p.set("city", location);
              }
              if (query || location) navigate(`/search?${p.toString()}`);
            }}
          />
        </div>
      </header>

      {/* Onglets de TYPE (Vente / Location / Services) — remplacent l'ancien
          bandeau « Impact Gively ». Filtrent les annonces par pilier ; l'onglet
          actif passe en violet. Le lien « Classement » est conservé à droite. */}
      <div className="container mx-auto px-4 pt-4 pb-2">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Parcourir par type</span>
          <Link to="/leaderboard" className="text-xs text-[#1DA1F2] font-medium hover:underline shrink-0 ml-3">
            Classement →
          </Link>
        </div>
        <ListingTypeTabs
          active={listingType}
          onSelect={(t) => { setListingType(t); setCategory(null); }}
        />
      </div>

      {/* Categories */}
      <div className="container mx-auto px-4 pt-2 pb-2">
        <CategoryPills
          autoScroll
          selected={category}
          onSelect={(c) => { setCategory(c); setListingType(null); pushRecentCat(c); }}
        />
      </div>

      {/* Annonces.
          - Vue par défaut (aucun filtre) : une LIGNE défilante par
            catégorie pour ne plus tout mélanger.
          - Vue filtrée (pill sélectionnée) : grille classique de la
            catégorie choisie. */}
      {listingType ? (
        <div id="home-listings-panel" role="tabpanel" className="container mx-auto px-4 pb-8" data-testid="home-type-view">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-slate-900">{TYPE_VIEW[listingType].title}</h2>
            <Link to={`/search?listing_type=${listingType}`} className="text-sm text-[#1DA1F2] font-medium hover:underline">
              Tout voir →
            </Link>
          </div>
          {typeLoading && typeListings.length === 0 ? (
            <div className={HOME_GRID}>
              {[...Array(HOME_PREVIEW_COUNT)].map((_, i) => <ListingCardSkeleton key={i} />)}
            </div>
          ) : typeListings.length > 0 ? (
            <div className={HOME_GRID} data-testid="type-listings-grid">
              {typeListings.slice(0, HOME_PREVIEW_COUNT).map((l) => <ListingCard key={l.listing_id} listing={l} />)}
            </div>
          ) : typeErrKind ? (
            <div data-testid="home-type-error">
              <ErrorState kind={typeErrKind} onRetry={() => window.location.reload()} />
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-2xl">
              <p className="text-5xl mb-4">{TYPE_VIEW[listingType].emoji}</p>
              <p className="text-slate-600 font-medium mb-2">{TYPE_VIEW[listingType].empty}</p>
              <p className="text-slate-400 text-sm mb-6">Soyez le premier à publier !</p>
              <Link to="/publish" className="btn-primary inline-block px-8 py-3 rounded-full">Publier une annonce</Link>
            </div>
          )}
        </div>
      ) : category ? (
        <div className="container mx-auto px-4 pb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-slate-900">Annonces</h2>
            <Link to={categoryHref(category)} className="text-sm text-[#1DA1F2] font-medium hover:underline">
              Tout voir →
            </Link>
          </div>
          {loading ? (
            <div className={HOME_GRID}>
              {[...Array(HOME_PREVIEW_COUNT)].map((_, i) => <ListingCardSkeleton key={i} />)}
            </div>
          ) : listings.length > 0 ? (
            <div className={HOME_GRID} data-testid="listings-grid">
              {listings.slice(0, HOME_PREVIEW_COUNT).map((l) => <ListingCard key={l.listing_id} listing={l} />)}
            </div>
          ) : featuredErrKind ? (
            <div data-testid="home-category-error">
              <ErrorState kind={featuredErrKind} onRetry={() => window.location.reload()} />
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-2xl">
              <p className="text-5xl mb-4">🛍️</p>
              <p className="text-slate-600 font-medium mb-2">Aucune annonce dans cette catégorie</p>
              <p className="text-slate-400 text-sm mb-6">Soyez le premier à publier !</p>
              <Link to="/publish" className="btn-primary inline-block px-8 py-3 rounded-full">Publier une annonce</Link>
            </div>
          )}
        </div>
      ) : (
        <div className="container mx-auto px-4 pb-8" data-testid="home-category-rows">
          {/* Ligne spéciale "favoris" : seulement quand l'inventaire est
              suffisant — sinon elle ferait doublon avec la grille compacte
              ci-dessous. */}
          {!lowInventory && favoritesRowD.length > 0 && (
            <ScrollRow
              title="Vos coups de cœur"
              seeAllTo="/favorites"
              listings={favoritesRowD}
            />
          )}
          {rowsLoading && homeRows.length === 0 ? (
            <div className={HOME_GRID}>
              {[...Array(HOME_PREVIEW_COUNT)].map((_, i) => <ListingCardSkeleton key={i} />)}
            </div>
          ) : lowInventory ? (
            // Inventaire faible → grande grille aérée (2 par rangée mobile),
            // aperçu de 4 rangées puis « Tout voir ».
            <>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-bold text-slate-900">Annonces récentes</h2>
                <Link to="/search" className="text-sm text-[#1DA1F2] font-medium hover:underline shrink-0 ml-3">
                  Tout voir →
                </Link>
              </div>
              <div className={HOME_GRID}>
                {allListings.slice(0, HOME_PREVIEW_COUNT).map((l) => (
                  <ListingCard key={l.listing_id} listing={l} />
                ))}
              </div>
              <CritereClassement className="mt-3 text-center" />
            </>
          ) : homeRowsD.length > 0 ? (
            <>
              {homeRowsD.map((row) => <CategoryRow key={row.category} row={row} />)}
              {autresAnnoncesD.length >= MIN_PER_ROW && (
                <ScrollRow title="Encore plus d'annonces" seeAllTo="/search" listings={autresAnnoncesD} />
              )}
              {/* Actions sous les annonces (Lucas, 22/10/2026). */}
    <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2" data-testid="hero-actions">
                <Link
                  to="/search"
                  className="bg-[#1DA1F2] text-white px-6 py-3 rounded-full font-bold text-center hover:bg-[#0d8bd9] transition"
                >
                  Voir les annonces
                </Link>
                <Link
                  to={user ? "/publish" : "/register"}
                  onClick={() => {
                    if (!user) memoriserRetour("/publish");
                  }}
                  className="border-2 border-[#1DA1F2] text-[#1DA1F2] px-6 py-3 rounded-full font-bold text-center hover:bg-sky-50 transition"
                >
                  Déposer une annonce
                </Link>
              </div>
              {/* D111-6 II : critère de tri des rangées ci-dessus. */}
              <CritereClassement className="mt-3 text-center" />
            </>
          ) : rowsErrKind ? (
            <div data-testid="home-rows-error">
              <ErrorState kind={rowsErrKind} onRetry={() => window.location.reload()} />
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-2xl">
              <p className="text-5xl mb-4">🛍️</p>
              <p className="text-slate-600 font-medium mb-2">Aucune annonce disponible</p>
              <p className="text-slate-400 text-sm mb-6">Soyez le premier à publier !</p>
              <Link to="/publish" className="btn-primary inline-block px-8 py-3 rounded-full">Publier une annonce</Link>
            </div>
          )}
        </div>
      )}

      {/* (Ancienne section « Location & services » retirée : redondante depuis
          les onglets de type Vente/Location/Services qui filtrent déjà ces
          annonces — décision Lucas.) */}

      {/* Bandeau « Rejoignez la communauté solidaire » SUPPRIMÉ définitivement
          (décision Lucas, août 2026 : « je veux plus jamais le revoir ») — ne
          pas le réintroduire. Le visiteur non connecté a déjà ses portes
          d'entrée : hero + recherche + nav Compte. */}

      {/* Phase 13.80 (retour Lucas mai 2026) — "Pourquoi Gively"
          repositionne ICI (entre annonces et partenaires) pour aerer
          la home : le visiteur a deja vu le produit (annonces), il
          comprend maintenant pourquoi Gively a une vraie identite
          ESS, puis on lui presente les partenaires assos. */}
      <WhyGivelyPillars />

      {/* Phase 9.1 — PartnersWidget redescendu ICI (après les annonces
          + CTA, avant le Blog) selon retour user : ordre final souhaité
          = titre → recherche → annonces → partenaires → blog → guides.
          Plus logique : les annonces sont la valeur produit principale,
          on montre les partenaires associatifs ensuite comme preuve
          de l'écosystème solidaire. */}
      <PartnersWidget />

      {/* Blog — 3 latest published posts. Hidden when empty.
          Fond bleu pastel très léger (retour Lucas) ; dégradé depuis le vert
          des partenaires pour adoucir la transition (pas de coupe nette). */}
      <div>
        <HomeBlogSection />
      </div>

      {/* « Guides et ressources » retiré de l'accueil (retour Lucas juin 2026 :
          une marketplace n'est pas un site vitrine — l'accueil sert à chercher /
          publier / acheter, pas à lister des liens SEO). Les pages guides
          existent toujours (routes indexées par Google, accessibles via la
          recherche) ; elles n'encombrent simplement plus l'écran d'accueil. */}

      {/* Phase 13.80 (retour Lucas mai 2026) — Encart presse "ils
          parlent de nous" redescendu tout en bas de la home. Avant :
          en haut, juste sous le hero. Apres : en pied de page, comme
          preuve sociale finale avant le mobile nav. */}
      <div className="py-8">
        <PressBadge className="" />
      </div>
      {/* Plus de spacer pb-24 ici : il créait une bande blanche vide entre
          la zone presse (violet) et le footer. Le footer global gère déjà
          sa propre marge basse (pb-20) pour dégager la nav mobile. */}
      {/* Phase 13.41 — PWAInstallPrompt retire d'ici car deja mount
          globalement dans App.js. Le doublon faisait incrementer le
          compteur visites 2 fois par page load. */}
    </div>
  );
};

// Encart « dons réalisés depuis le lancement » affiché sous le hero, en
// remplacement de l'ancienne frise « comment ça marche » (jugée peu lisible).
// Met en avant la preuve sociale + la signature solidaire : une part de
// chaque transaction finance l'association choisie.
// Source : /stats/yearly-impact (cumul all-time, déjà mis en cache côté back).
// Lecture synchrone du cache sessionStorage pour éviter le flash blanc.
const HeroDonationCounter = () => {
  const [data, setData] = useState(() => {
    try {
      const cached = safeSessionStorage.getItem("gively_impact");
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    api.get("/stats/yearly-impact", { timeout: 5000 }).then((r) => {
      setData(r.data);
      try { safeSessionStorage.setItem("gively_impact", JSON.stringify(r.data)); } catch { /* storage indispo */ }
    }).catch(() => { /* on garde le cache existant ou le fallback promesse */ });
  }, []);

  const count = data?.total_donations_count ?? 0;
  const amount = Math.round(data?.total_donated ?? 0);
  // Preuve sociale affichée SEULEMENT à partir de 300 € collectés (décision
  // Lucas) : en dessous, un compteur « 6 € collectés » dessert l'image → on
  // montre la promesse à la place. Le compteur réapparaît automatiquement
  // dès que le cumul atteint 300 €.
  const REVEAL_THRESHOLD_EUR = 300;
  const hasDons = amount >= REVEAL_THRESHOLD_EUR;

  // Sous 300 € collectés : on n'affiche PAS le petit montant (« 6 € collectés »
  // desservirait l'image), mais on ne laisse PLUS un VIDE — le solidaire est l'USP
  // de Gively, il doit être visible au-dessus de la ligne de flottaison dès le
  // lancement (les marketplaces de référence affichent leur impact en permanence).
  // On montre donc la PROMESSE (message d'amorçage) ; le compteur chiffré réapparaît
  // seul dès ≥ 300 €.
  if (!hasDons) {
    // Amorçage < 300 € : RETIRÉ (demande Lucas, 28/06 — jugé inesthétique sous le
    // sous-titre du hero). On n'affiche RIEN tant qu'il n'y a pas de montant réel
    // collecté ; le compteur chiffré ci-dessous prend le relais dès ≥ 300 €.
    return null;
  }

  return (
    <Link
      to="/associations"
      className="group inline-flex items-center gap-2.5 mb-6 max-w-full rounded-full border border-emerald-200 bg-gradient-to-r from-emerald-50 to-green-50 px-4 py-2 transition-colors hover:border-emerald-300 hover:from-emerald-100/70"
      aria-label={`${count} dons versés aux associations depuis le lancement de Gively`}
    >
      <svg
        className="h-4 w-4 shrink-0 text-emerald-600"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
      >
        <path d="M12 21s-6.716-4.297-9.428-7.01C.86 12.278.5 9.4 2.1 7.6 3.6 5.9 6.3 5.8 8 7.3l.9.8.9-.8c1.7-1.5 4.4-1.4 5.9.3 1.6 1.8 1.24 4.678-.472 6.39C18.716 16.703 12 21 12 21z" />
      </svg>
      <span className="text-left text-sm text-slate-700">
        <strong className="font-bold text-emerald-700">{count.toLocaleString("fr-FR")}</strong>{" "}
        {count > 1 ? "dons versés" : "don versé"} aux associations
        {amount > 0 && (
          <>
            {" · "}
            <strong className="font-bold text-emerald-700">{amount.toLocaleString("fr-FR")} €</strong>{" "}
            collectés
          </>
        )}{" "}
        <span className="text-slate-500">depuis le lancement</span>
      </span>
    </Link>
  );
};

// "Pourquoi Gively" — 4 piliers ESS. Refonte mai 2026 (retour Lucas) :
// retrait des emoticones au profit d'illustrations photo (libres de
// droits, hostees Unsplash CDN) + une couleur par carre (palette
// Gively : bleu / vert / violet + accent blanc). Section placee entre
// les annonces et les partenaires pour aerer la page.
const WhyGivelyPillars = () => {
  const pillars = [
    {
      // Recyclage textile - photo Unsplash libre de droits
      img: "https://images.unsplash.com/photo-1489274495757-95c7c837b101?auto=format&fit=crop&w=600&q=65",
      alt: "Vêtements pliés représentant le réemploi textile",
      title: "Économie circulaire",
      desc: "Donner une 2ᵉ vie aux objets plutôt que d'acheter neuf : vêtements, mobilier, électronique reconditionné.",
      tint: "from-sky-50 to-blue-100",
      border: "border-sky-200",
      titleColor: "text-sky-900",
    },
    {
      // Mains qui s'unissent (la celebre photo Unsplash)
      img: "https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?auto=format&fit=crop&w=600&q=65",
      alt: "Mains qui se joignent symbolisant l'entraide",
      title: "Économie solidaire",
      desc: "Chaque transaction (vente, location ou service) verse une part à l'association choisie.",
      tint: "from-emerald-50 to-green-100",
      border: "border-emerald-200",
      titleColor: "text-emerald-900",
    },
    {
      // Echange de colis en main propre — photo fournie par Lucas
      // (rangee localement dans /public/illustrations pour zero
      // dependance externe et zero risque de cassure CDN).
      img: "/illustrations/economie-locale.webp",
      alt: "Colis échangé en main propre entre deux personnes",
      title: "Économie locale",
      desc: "Vendeurs et acheteurs près de chez vous : rencontre et remise en main propre possibles.",
      tint: "from-violet-50 to-purple-100",
      border: "border-violet-200",
      titleColor: "text-violet-900",
    },
    {
      // Feuilles vertes / nature
      img: "https://images.unsplash.com/photo-1444492417251-9c84a5fa18e0?auto=format&fit=crop&w=600&q=65",
      alt: "Feuilles vertes représentant l'impact environnemental",
      title: "Impact environnemental",
      desc: "Réemploi = moins de neuf, moins d'enfouissement, moins de CO₂.",
      tint: "from-white to-slate-50",
      border: "border-slate-200",
      titleColor: "text-slate-900",
    },
  ];
  return (
    <section
      className="py-8"
      aria-labelledby="why-gively-title"
    >
      <div className="container mx-auto px-4">
        <div className="text-center mb-5">
          <p className="text-xs font-semibold text-[#1DA1F2] uppercase tracking-wider mb-1">
            Notre identité
          </p>
          <h2 id="why-gively-title" className="text-xl sm:text-2xl font-extrabold text-slate-900">
            Pourquoi Gively n'est pas une marketplace comme les autres
          </h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {pillars.map((p) => (
            <article
              key={p.title}
              className={`bg-gradient-to-br ${p.tint} border ${p.border} rounded-2xl overflow-hidden hover:shadow-md transition-shadow flex flex-col`}
            >
              {/* Photo Unsplash libre de droits, ratio 16/10 mobile.
                  Fallback : si la photo casse, on remplace par un
                  degradé pastel avec la 1re lettre du titre, plutot
                  que de cacher l'image (carte vide). */}
              <div className={`relative aspect-[16/10] overflow-hidden bg-gradient-to-br ${p.tint}`}>
                <img
                  src={p.img}
                  alt={p.alt}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const el = e.currentTarget;
                    el.style.display = "none";
                    if (el.nextSibling) el.nextSibling.style.display = "flex";
                  }}
                />
                <div
                  className={`absolute inset-0 hidden items-center justify-center text-4xl font-extrabold opacity-30 ${p.titleColor}`}
                  aria-hidden="true"
                >
                  {p.title.charAt(0)}
                </div>
              </div>
              <div className="p-3 sm:p-4 flex-1">
                <h3 className={`text-sm sm:text-base font-bold mb-1 leading-snug ${p.titleColor}`}>
                  {p.title}
                </h3>
                <p className="text-xs sm:text-[13px] text-slate-600 leading-snug">{p.desc}</p>
              </div>
            </article>
          ))}
        </div>
        {/* 26/10/2026 (Lucas) : l'Impact Score en bandeau fin sous les 4 cartes. */}
        <ImpactScoreEncart />
      </div>
    </section>
  );
};

export default HomePage;
